import React, { useState, useEffect, useCallback } from "react";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { Lock } from "react-feather";
import { useTheme } from "../theme/ThemeContext";
import { useSettings } from "../settings/SettingsContext";

import { API_BASE, apiFetch } from "../api";

// Last 4 completed months + the current month + the next (upcoming) month -
// matches the trailing-history-plus-next-cycle shape the rest of the app uses.
const getMonthWindow = (referenceDate = new Date()) => {
  const months = [];
  for (let i = 4; i >= -1; i--) {
    const d = new Date(referenceDate.getFullYear(), referenceDate.getMonth() - i, 1);
    months.push({ year: d.getFullYear(), month: d.getMonth(), label: d.toLocaleDateString("en-US", { month: "short" }) });
  }
  return months;
};

const monthKey = (dateStr) => {
  const d = new Date(`${dateStr}T00:00:00`);
  return `${d.getFullYear()}-${d.getMonth()}`;
};

const buildChartData = (bills, income) => {
  const months = getMonthWindow();
  let running = 0;
  return months.map(({ year, month, label }) => {
    const key = `${year}-${month}`;
    const salary = income.filter((i) => monthKey(i.date) === key).reduce((sum, i) => sum + i.amount, 0);
    const outflow = bills.filter((b) => monthKey(b.dueDate) === key).reduce((sum, b) => sum + b.amount, 0);
    running += salary - outflow;
    return { month: label, salary, outflow, inHand: running };
  });
};

const CashflowCard = () => {
  const { tokens, fonts } = useTheme();
  const { settings } = useSettings();
  const masked = settings.maskAmounts;
  const [bills, setBills] = useState([]);
  const [income, setIncome] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(false);
    Promise.all([
      apiFetch(`${API_BASE}/bills`).then((res) => (res.ok ? res.json() : Promise.reject())),
      apiFetch(`${API_BASE}/income`).then((res) => (res.ok ? res.json() : Promise.reject())),
    ])
      .then(([billsData, incomeData]) => {
        setBills(billsData);
        setIncome(incomeData);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const chartData = buildChartData(bills, income);

  return (
    <div style={{ ...styles.card, backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, boxShadow: tokens.cardShadow }}>
      <div style={{ ...styles.cardRow, borderBottom: `1px solid ${tokens.surfaceBorder}` }}>
        <p style={{ ...styles.cardTitle, fontFamily: fonts.display, color: tokens.textPrimary }}>Cashflow Overview</p>
      </div>

      {loading && <p style={{ color: tokens.textMuted, fontSize: 13, margin: 0 }}>Loading...</p>}
      {error && (
        <p style={{ color: tokens.negative, fontSize: 13, margin: 0 }}>
          Couldn't reach the backend. Run it with `npm run server`.
        </p>
      )}

      {!loading && !error && masked && (
        <div
          style={{
            height: 260,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            borderRadius: 12,
            backgroundColor: tokens.neutralSoft,
          }}
        >
          <Lock size={22} color={tokens.textMuted} />
          <span style={{ fontSize: 13, color: tokens.textMuted, fontWeight: 600 }}>Amounts hidden</span>
          <span style={{ fontSize: 12, color: tokens.textMuted }}>Turn off Privacy Mode in Settings to view</span>
        </div>
      )}

      {!loading && !error && !masked && (
        <div style={{ height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={tokens.chartGrid} />
              <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fill: tokens.textMuted, fontSize: 12 }} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: tokens.textMuted, fontSize: 12 }} />
              <Tooltip
                cursor={{ fill: tokens.chartGrid }}
                formatter={(value) => `₹${value.toLocaleString()}`}
                contentStyle={{ backgroundColor: tokens.surface, border: `1px solid ${tokens.surfaceBorder}`, borderRadius: 10, color: tokens.textPrimary, boxShadow: tokens.cardShadow }}
                labelStyle={{ color: tokens.textPrimary, fontWeight: 700 }}
              />
              <Legend
                verticalAlign="top"
                align="right"
                height={32}
                iconType="circle"
                iconSize={8}
                wrapperStyle={{ color: tokens.textMuted, fontSize: 12.5, fontWeight: 600 }}
              />
              <Bar dataKey="salary" fill={tokens.chartInflow} name="Income" radius={[4, 4, 0, 0]} barSize={22} />
              <Bar dataKey="outflow" fill={tokens.chartOutflow} name="Total Outflow" radius={[4, 4, 0, 0]} barSize={22} />
              <Line type="monotone" dataKey="inHand" stroke={tokens.chartLine} strokeWidth={2.5} name="Net Cashflow" dot={{ fill: tokens.chartLineDot, r: 4, strokeWidth: 0 }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const styles = {
  card: {
    padding: 20,
    borderRadius: 16,
    display: "flex",
    flexDirection: "column",
    gap: 16,
    minHeight: 300,
    transition: "background-color 0.2s ease, border-color 0.2s ease",
  },
  cardTitle: {
    fontSize: 15,
    fontWeight: "700",
    margin: 0,
  },
  cardRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    paddingBottom: 8,
  },
};

export default CashflowCard;
